import React, { ClipboardEvent, FC } from "react";
import { UseFormRegisterReturn } from "react-hook-form";
import styled, { css } from "styled-components";
import { formInputStyle, formLabelStyle, formField } from "styles/CommonStyles";
import TooltipButton from "~/components/atoms/TooltipButton";
import FormErrorMessage from "~/components/atoms/form/FormErrorMessage";
import FormAttention, { AttentionProps } from "./FormAttention";

interface Props {
  ariaLabel?: string; // using aria-label when input does not has a visible label
  attention?: AttentionProps;
  errorMessage?: string;
  id: string;
  isBorderless?: boolean;
  label?: string;
  name?: string;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onPaste?: (event: ClipboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  register?: UseFormRegisterReturn;
  rows?: number;
  tooltip?: string;
  value?: string;
}

// auto resize text area height
const adjustTextarea = (
  event: React.ChangeEvent<HTMLTextAreaElement>,
  minRows: number
) => {
  const TEXTAREA_LINE_HEIGHT = 30;
  // reset textarea height
  event.target.rows = minRows;
  // get the real rows
  const currentRows = ~~(event.target.scrollHeight / TEXTAREA_LINE_HEIGHT);
  // set new rows value
  if (currentRows > minRows) {
    event.target.rows = currentRows;
  }
};

const TextAreaField: FC<Props> = ({
  ariaLabel,
  attention,
  errorMessage,
  id,
  isBorderless,
  label,
  name,
  onBlur,
  onChange,
  onPaste,
  placeholder,
  register,
  rows = 6,
  tooltip,
  value,
}) => (
  <div>
    <FormField>
      {label && <LabelText htmlFor={id}>{label}</LabelText>}
      {attention && <FormAttention {...attention} />}
      {tooltip && <TooltipButton text={tooltip} />}
    </FormField>
    <TextArea
      aria-label={ariaLabel}
      className="light-placeholder"
      id={id}
      isBorderless={isBorderless}
      defaultValue={value}
      hasDataError={!!errorMessage}
      name={name || register?.name}
      onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
        register?.onChange && register.onChange(event);
        onChange && onChange(event);
        adjustTextarea(event, rows);
      }}
      onBlur={(event: React.FocusEvent<HTMLTextAreaElement>) => {
        register?.onBlur && register.onBlur(event);
        onBlur && onBlur(event);
      }}
      onFocus={(event: React.FocusEvent<HTMLTextAreaElement>) =>
        adjustTextarea(event, rows)
      }
      onPaste={onPaste}
      placeholder={placeholder}
      ref={register?.ref}
      rows={rows}
    />
    {errorMessage && <FormErrorMessage errorMessage={errorMessage} />}
  </div>
);

const LabelText = styled.label`
  ${formLabelStyle};
`;

const FormField = styled.div`
  ${formField}
`;

const TextArea = styled.textarea<{
  hasDataError?: boolean;
  isBorderless?: boolean;
}>`
  ${formInputStyle};
  font-family: inherit;
  overflow: hidden;
  resize: none;

  ${({ isBorderless }) => {
    if (isBorderless) {
      return css`
        border: none;
      `;
    }
  }};
`;

export default TextAreaField;
